// Geometry edit reducer cases that operate as pure coordinate
// transforms over the selection (MOVE, COPY, COPY-BASE, MIRROR,
// ROTATE, SCALE, OFFSET). Extracted from geometryReducer.ts as part
// of the PR-0 mechanical decomposition.
//
// ⚠ deps.md Risk #1 — `lineLineIntersection` shadow: RETIRED.
// The OFFSET LWPOLYLINE branch used to consume a 2-arg, object-segment
// `lineLineIntersection(seg1, seg2)` declared file-locally here (a
// verbatim re-declaration of the pre-split geometryReducer.ts binding,
// which shadowed the 4-arg `@dxf-ai-editor/command-engine` export).
// @codex PR #785 round 16, FINDING 1 moved the whole polyline offset ring
// into `command-engine/polylineOffsetRing.ts`, which was the shadow's ONLY
// caller, so the local declaration is gone and NO `lineLineIntersection`
// binding exists in this file at all. The ring calls the package's own
// 4-arg export — the same closed form in the same operand order, so no
// corner of a straight polyline moves by one ULP. The sibling
// `geometryReducerEditTrim.ts` retired its own shadow earlier and is
// unaffected.
//
// Returns `null` when the dispatched command is not handled here so
// the chain in `geometryReducer.ts` falls through to the next
// sub-reducer.
//
// Phase 3b PR #2 — migrated to dual-export `ReducerMutationResult`
// contract. `applyEditActionWithMutations` is the canonical (and now sole)
// entry point. Each case populates `mutations.added` / `mutations.removed`
// / `mutations.updated` against the FINAL re-validated document so prev/next
// references match what consumers will see post-commit (Zod parse breaks
// reference identity, so we look up by id in the post-assemble result).
//
// Phase 6 (Track 1 cleanup) — the legacy `applyEditAction` thin wrapper
// has been removed. `geometryReducer.ts` invokes
// `applyEditActionWithMutations` directly (since PR #5).
import {
  assembleDrawingDocumentInPlace,
  dimensionSubtypeBaseAngleDeg,
  dimensionSubtypeNibble,
  type DrawingDocument,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  heavyPolylineDrawnChain,
  isDegenerateMirrorAxis,
  isOleFrameTransformRefused,
  isSupportedHeavyPolylineCurve,
  isTableMirrorRefused,
  isTableScaleRefused,
  mirrorAxisAngleDeg as mirrorAxisAngleDegFn,
  mirrorFullReflectRelativeAngleField,
  mirrorPoint,
  mirrorReadableRelativeAngleField,
  mirrorRotationField,
  mirrorRotationFieldRadians,
  mirrorTextFull,
  mirrorTextReadable,
  negateVectorXY,
  offsetCircle,
  offsetLineSegment,
  offsetPolylineVertexRing,
  presencePreservingRotation,
  readableAngleAcrossAxis,
  reflectExtrusion,
  reflectVectorXY,
  rotateEntityAngles,
  rotatePoint,
  scaleEntityRadii,
  scalePoint,
  TEXT_GEN_FLAG_UPSIDE_DOWN,
  transformEntityCoords,
  translatePoint,
  withWorldAttributeGenerationFlags,
  withWorldAttributeRotation,
} from "@dxf-ai-editor/command-engine";
import type {
  DynamicBlockCommand,
  EditorCommand,
} from "@dxf-ai-editor/contracts";

import {
  appendGeometryEntities,
  cloneEntityWithFreshIdentity,
  createEntityIdAllocator,
} from "./geometryReducerHelpers";
import {
  NO_MUTATIONS,
  type EntityMutation,
  type ReducerMutationResult,
  type StyleTableTouchKind,
} from "./types";

/**
 * Resolve a Dimension's DIMTIH (DXF group 73) for the MIRROR reducer's
 * readable-mode (MIRRTEXT=0) `textAngleRelativeToDimLine` base-angle
 * derivation (`mirrorReadableRelativeAngleField`, GitHub Codex review PR
 * #558 round 2 P1) — mirrors the render path's OWN resolution EXACTLY
 * (`dimensionHelpers.ts::resolveDimensionStyleRow`: named-table lookup,
 * "Standard" fallback, `DIMTIH` defaults `true` when the resolved row is
 * absent or doesn't carry it, matching `model.ts`'s own
 * `dimensionStyleSchema: DIMTIH: z.boolean().default(true)`). Deliberately
 * NOT imported from `scene/dimensionHelpers.ts` (a WebGL-adjacent render
 * module) — the state layer resolves this itself, case-sensitive + exact
 * name match like the render path, NOT the case-insensitive match
 * `geometry/create-dimension` uses at creation time (a DIFFERENT, unrelated
 * DIMSTYLE-name lookup elsewhere in this file).
 */
function resolveDimtih(
  geometryDocument: DrawingDocument,
  styleName: string,
): boolean {
  const styles = geometryDocument.styleTables.dimensionStyles;
  const row =
    styles.find((s) => s.name === styleName) ??
    styles.find((s) => s.name === "Standard");
  return row?.DIMTIH ?? true;
}

/**
 * GitHub Codex review PR #558 round 2 (round-17 local read-only follow-up)
 * P2 — properly reflect a MLeader CONTEXT_DATA `mTextData.extrusion` across
 * the mirror axis via {@link reflectExtrusion} (command-engine, the SAME
 * general reflection real MTEXT's own `mirrorTextFull` uses — preserves an
 * arbitrary OCS tilt, unlike the prior code's canonicalisation to the
 * axis-aligned `(0,0,±1)` pair). Presence-preserving (`.optional()`, never
 * `.default()` — byte-faithfulness): when the reflected vector reduces to
 * the EXACT canonical default `{0,0,1}` (the untilted/unmirrored common
 * case, verified bit-exact — negating `-1` gives exactly `1`, and `0 * any
 * trig value` is exactly `0`), returns `undefined` to OMIT the field rather
 * than materialise a spurious explicit default, matching the pre-fix
 * code's own "delete when unmirrored" branch for that ONE case it
 * correctly handled; a genuinely tilted result (any OTHER vector, including
 * the canonical `(0,0,-1)` mirrored case) stays materialised explicitly.
 */
function reflectMLeaderExtrusion(
  extrusion: { x: number; y: number; z: number } | undefined,
  axisAngleDeg: number,
): { x: number; y: number; z: number } | undefined {
  const reflected = reflectExtrusion(extrusion, axisAngleDeg);
  // `-0` normalisation: `reflectExtrusion`'s own `-M·v` sign-flip can yield
  // a negative-zero component (e.g. negating an already-zero X/Y) — bit-
  // identical to `+0` for every numeric/DXF-serialisation purpose (`-0 ===
  // 0` is `true`), but distinguishable by strict deep-equality (`Object.is`
  // -aware matchers). `+ 0` is the standard JS idiom to collapse `-0` to
  // `+0` (addition's IEEE754 rounding rule makes `-0 + 0 === 0`, never
  // `-0`).
  const x = reflected.x + 0;
  const y = reflected.y + 0;
  const z = reflected.z + 0;
  return x === 0 && y === 0 && z === 1 ? undefined : { x, y, z };
}

/**
 * GitHub Codex review PR #558 round 38 (formal bot review, HEAD ecca1c8d)
 * P2 — "Mirror MLeader direction vectors in readable mode". Completes the
 * `mTextData.textDirection` handling `reflectMLeaderExtrusion`'s sibling
 * blocks below started (round with HEAD 2d0dd928, FULL-mirror/MIRRTEXT=1
 * only) by covering the READABLE (MIRRTEXT=0) case those blocks explicitly
 * deferred at the time as "no established reference to validate against".
 * That reference exists in this codebase already: `geometryTransforms.ts`'s
 * `mirrorTextReadable` (standalone MTEXT's own readable-mirror handler) —
 * for its `hasCustomDir` branch it derives the readability-flip decision
 * from the DIRECTION VECTOR's own angle (`atan2(dir.y, dir.x)`), NOT from
 * the entity's separate `rotation` scalar, then negates the geometrically-
 * reflected vector's XY components exactly when that flip fired
 * (`negateVectorXY`, exported this same round specifically for this reuse).
 * This is the correct source angle (not MLeader's own `mTextData.rotation`)
 * because ezdxf documents the direction vector as taking priority OVER
 * rotation whenever both are present (see `reflectVectorXY`'s own doc) — so
 * the vector's OWN angle, not a possibly-divergent rotation field, is what
 * "reads backward after reflection" actually means for a MLeader with an
 * explicit textDirection. `readableAngleAcrossAxis` (degrees, exported from
 * `mirrorAngleMath.ts`) is the SAME `{angle, flipped}` primitive
 * `mirrorTextReadable` itself calls — byte-identical formula, MLeader-typed
 * call site.
 */
function reflectMLeaderTextDirectionReadable(
  textDirection: { x: number; y: number; z: number },
  mirrorAxisAngleDeg: number,
): { x: number; y: number; z: number } {
  const sourceAngleDeg =
    (Math.atan2(textDirection.y, textDirection.x) * 180) / Math.PI;
  const { flipped } = readableAngleAcrossAxis(
    sourceAngleDeg,
    mirrorAxisAngleDeg,
  );
  const reflected = reflectVectorXY(textDirection, mirrorAxisAngleDeg);
  return flipped ? negateVectorXY(reflected) : reflected;
}

/** Build an id → entity (with spaceId) lookup over a document. */
function indexEntitiesById(
  document: DrawingDocument,
): Map<string, { spaceId: string; entity: DrawingEntity }> {
  const idx = new Map<string, { spaceId: string; entity: DrawingEntity }>();
  for (const space of document.spaces) {
    for (const entity of space.entities) {
      idx.set(entity.id, { spaceId: space.id, entity });
    }
  }
  return idx;
}

/**
 * Construct an `EntityMutation` from id arrays measured against the
 * pre- and post-assemble documents. `addedIds` / `removedIds` /
 * `updatedIds` are computed by each case; this helper resolves the
 * concrete entity payloads (post-Zod-parse references) so consumers
 * see exactly what `commitMutation` will write into the index.
 *
 * Returns `NO_MUTATIONS` when all three id sets are empty so the
 * pipeline fast-paths into a true no-op.
 */
function buildGeometryMutations(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
  addedIds: ReadonlyArray<string>,
  removedIds: ReadonlyArray<string>,
  updatedIds: ReadonlyArray<string>,
): EntityMutation {
  if (
    addedIds.length === 0 &&
    removedIds.length === 0 &&
    updatedIds.length === 0
  ) {
    return NO_MUTATIONS;
  }
  const prevIndex = indexEntitiesById(prevDoc);
  const nextIndex = indexEntitiesById(nextDoc);

  const added: Array<{ spaceId: string; entity: DrawingEntity }> = [];
  for (const id of addedIds) {
    const after = nextIndex.get(id);
    if (!after) continue;
    added.push({ spaceId: after.spaceId, entity: after.entity });
  }

  const removed: Array<{ spaceId: string; entityId: string }> = [];
  for (const id of removedIds) {
    const before = prevIndex.get(id);
    if (!before) continue;
    removed.push({ spaceId: before.spaceId, entityId: id });
  }

  const updated: Array<{
    spaceId: string;
    prev: DrawingEntity;
    next: DrawingEntity;
  }> = [];
  for (const id of updatedIds) {
    const before = prevIndex.get(id);
    const after = nextIndex.get(id);
    // Defensive: if an id reported as updated isn't present in either
    // index something upstream is inconsistent — skip rather than throw
    // so a single bad id doesn't break the whole batch.
    if (!before || !after) continue;
    updated.push({
      spaceId: after.spaceId,
      prev: before.entity,
      next: after.entity,
    });
  }

  return {
    added,
    removed,
    updated,
    styleTablesTouched: new Set<StyleTableTouchKind>(),
  };
}

/**
 * T6d-1: shared in-place transform for the edit family that mutates entities
 * coordinate-wise WITHOUT add/remove/reorder (MOVE, ROTATE, SCALE,
 * MIRROR-deleteSource). Captures the touched prev entity + its position during
 * the single transform map, then resolves the post-assemble `next` reference
 * straight from `nextDoc` at the SAME position (assembleDrawingDocumentInPlace
 * preserves entity order for in-place edits). This produces an identical
 * `EntityMutation` to routing through buildGeometryMutations, but in O(touched)
 * instead of 2×O(N) full-document index walks.
 *
 * BYTE-IDENTITY: `assembleDrawingDocumentInPlace` preserves entity order — PROD
 * returns `next` unchanged; dev/test maps entities position-for-position,
 * re-parsing only the changed ids. So `nextDoc.spaces[spaceIndex].entities[
 * entityIndex]` is the SAME post-assemble reference `buildGeometryMutations`
 * would have resolved via `nextIndex.get(id)`, and `prev` is the exact
 * pre-transform reference (same as `prevIndex.get(id).entity`). The resulting
 * `EntityMutation.updated` is element-for-element identical (same spaceId, same
 * prev/next refs) with empty `added`/`removed` in both paths → `commitMutation`
 * writes identical data → byte-identical DXF output + identical undo/redo.
 *
 * DECLINING A SELECTED ENTITY: `transform` may return `null` to mean "this
 * edit does not apply to this entity; leave it exactly as it is". A declined
 * entity keeps its ORIGINAL reference in the assembled document, is NOT listed
 * in `changedIds` for `assembleDrawingDocumentInPlace`, and produces NO
 * `updated` row — so it contributes nothing to the `EntityMutation`, nothing
 * to `commitMutation`'s writes and nothing to the history entry, and the undo
 * of the surrounding command therefore restores it trivially (it never moved).
 * Used by `geometry/scale-entities`' `Table` refusal (GitHub Codex review PR
 * #785 round 2, P2 — see `command-engine/tableScaleRefusal.ts`). Returning the
 * input reference unchanged would NOT be equivalent: it would still record a
 * prev→next `updated` row and bump the revision for an entity nothing touched.
 */
function applyInPlaceTransformWithMutations(
  geometryDocument: DrawingDocument,
  idsToTransform: ReadonlySet<string>,
  transform: (entity: DrawingEntity) => DrawingEntity | null,
): ReducerMutationResult {
  const touched: Array<{
    spaceId: string;
    spaceIndex: number;
    entityIndex: number;
    prev: DrawingEntity;
  }> = [];
  const nextDoc = assembleDrawingDocumentInPlace(
    {
      ...geometryDocument,
      spaces: geometryDocument.spaces.map((space, spaceIndex) => ({
        ...space,
        entities: space.entities.map((entity, entityIndex) => {
          if (!idsToTransform.has(entity.id)) return entity;
          const next = transform(entity);
          // `null` = the edit DECLINED this entity (see the doc comment): keep
          // the original reference and record no mutation for it.
          if (next === null) return entity;
          touched.push({ spaceId: space.id, spaceIndex, entityIndex, prev: entity });
          return next;
        }),
      })),
    },
    touched.map((t) => t.prev.id),
  );
  if (touched.length === 0) {
    return { document: nextDoc, mutations: NO_MUTATIONS };
  }
  const updated = touched.map((t) => ({
    spaceId: t.spaceId,
    prev: t.prev,
    next: nextDoc.spaces[t.spaceIndex].entities[t.entityIndex],
  }));
  return {
    document: nextDoc,
    mutations: {
      added: [],
      removed: [],
      updated,
      styleTablesTouched: new Set<StyleTableTouchKind>(),
    },
  };
}

/**
 * One-truth helper for the "duplicate the selected entities through a
 * per-entity transform, keep the sources, add the clones" family — COPY
 * (`copy-entities`), COPY-BASE (`copy`), and MIRROR-keep-source
 * (`mirror-entities` with `deleteSource === false`). All three walk the
 * document in feed order, apply `transform` to each selected entity, mint a
 * fresh id, and append the whole batch.
 *
 * T6a perf: ids are minted via a once-seeded {@link createEntityIdAllocator}
 * (amortized O(1), reproducing `nextGeometryEntityId`'s exact sequence) and
 * the clones are appended with a SINGLE {@link appendGeometryEntities}
 * validation pass — O(N) for the batch instead of O(N·K) ≈ O(N²). The id
 * prefix is `entity.type.toLowerCase().replace(/\s+/g, "-")`, identical to the
 * prior per-site loops, so the minted ids (and thus the exported DXF group-5
 * handles) are byte-identical.
 *
 * `transform` receives the matched source entity and returns the clone WITHOUT
 * an id (or with one that will be overwritten) — the helper stamps the minted
 * id. The mutation payload is `added`-only (sources are untouched), built via
 * {@link buildGeometryMutations} against the pre/post documents.
 *
 * ## `null` = this edit DECLINED this entity — make NO clone
 *
 * @codex PR #785 round 16, FINDING 2 ("Skip declined tables in mirror-copy").
 * `transform` used to have no way to say that, so a MIRROR with "keep source"
 * over a refusal — an `ACAD_TABLE` under any reflection, an OLE2FRAME under a
 * slanted one — appended a BYTE-IDENTICAL copy of the source stacked on the
 * original at a fresh id, while the shell reducer's audit row said the entity
 * had been declined. Those two statements contradict each other, and the
 * duplicate is the one the user has to find and erase.
 *
 * Returning `null` skips the entity entirely: no id is minted for it (so the
 * remaining clones keep the dense sequence a smaller batch would have had), no
 * clone is appended, and nothing enters `added`. That mirrors
 * {@link applyInPlaceTransformWithMutations}' own `null` contract, so ONE
 * refusal test in a per-entity transform serves both the in-place and the
 * keep-source halves of the same command.
 */
function cloneSelectionWithMutations(
  geometryDocument: DrawingDocument,
  idsToClone: Set<string>,
  transform: (entity: DrawingEntity) => DrawingEntity | null,
): ReducerMutationResult {
  const idAllocator = createEntityIdAllocator(geometryDocument);
  const clones: DrawingEntity[] = [];
  const addedIds: string[] = [];
  for (const space of geometryDocument.spaces) {
    for (const entity of space.entities) {
      if (!idsToClone.has(entity.id)) continue;
      const cloned = transform(entity);
      // `null` = the edit DECLINED this entity (see the doc comment): append
      // no clone and mint no id for it.
      if (cloned === null) continue;
      const typePrefix = entity.type.toLowerCase().replace(/\s+/g, "-");
      const newId = idAllocator.next(typePrefix);
      // T9b review round 3 — fresh own-record identity (no shared seqendHandle).
      clones.push(cloneEntityWithFreshIdentity(cloned, newId));
      addedIds.push(newId);
    }
  }
  const nextDoc = appendGeometryEntities(geometryDocument, clones);
  return {
    document: nextDoc,
    mutations: buildGeometryMutations(
      geometryDocument,
      nextDoc,
      addedIds,
      [],
      [],
    ),
  };
}

/**
 * Phase 3b PR #2 — mutation-aware variant of `applyEditAction`.
 *
 * Returns `{ document, mutations }` for any of the 7 edit cases this
 * file owns. Returns `null` for unrelated commands so the chain in
 * `geometryReducer.ts` keeps falling through to the next sub-reducer.
 *
 * Mutation summary per case:
 *   - move-entities      → `updated` (one per affected entity)
 *   - rotate-entities    → `updated`
 *   - scale-entities     → `updated`
 *   - mirror-entities (deleteSource=true)  → `updated` (in-place flip)
 *   - copy-entities      → `added` (clones); sources unchanged
 *   - copy (base/second) → `added`; sources unchanged
 *   - mirror-entities (deleteSource=false) → `added` (mirrored copies);
 *                          sources unchanged
 *   - offset-entity      → `added` (one offset clone, when supported)
 *
 * `prev` / `next` payloads are read from the post-assemble document so
 * they match the references `commitMutation` will install in the
 * cache + index — the assembler re-parses (in dev/test) which rebuilds
 * objects, breaking referential identity. T6c: the in-place arms (MOVE /
 * ROTATE / SCALE / MIRROR-deleteSource) use `assembleDrawingDocumentInPlace`,
 * which re-parses ONLY the changed entities (those whose ids it is passed) and
 * skips the whole-document walk; the result for the touched entities is
 * byte-identical to the full parse, so `buildGeometryMutations` sees the same
 * post-assemble references either way.
 */
export function applyEditActionWithMutations(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
): ReducerMutationResult | null {
  switch (command.type) {
    // ── Modification commands: MOVE, COPY, MIRROR ─────────────────

    case "geometry/move-entities": {
      const { entityIds, delta } = command;
      // Phase 4 Wave 2 Task 4 (codex F1 follow-up) — preserve feed
      // (DRAWORDER) ordering. An earlier revision walked `forEachLive`
      // per `CompactStoreKind` and rebuilt `space.entities` from the
      // compact cache. That grouped the output by store kind (LINE
      // first, then POINT, …) and broke the original DXF feed order
      // that SceneBulkLoad treats as z-order — users observed stacking
      // changes after MOVE.
      //
      // The shared in-place helper keeps the classic `space.entities.map`
      // ordering intact (it never groups by store kind). The compact cache
      // stays in sync transparently: `commitMutation` (called downstream by
      // `editorStore.dispatch`) routes each `mutations.updated` entry
      // through `setInCompactCache`, so the column-aware store sees the
      // transformed entity without this reducer touching it directly.
      // T6c incremental validation: MOVE is a pure in-place coordinate
      // transform — it adds/removes no entity and changes no id/spaceId, so
      // the cross-entity invariants are preserved by construction. T6d-1:
      // routed through `applyInPlaceTransformWithMutations`, which captures the
      // touched prev/next in O(touched) (no full-document index build) and
      // re-validates ONLY the moved entities via the fast-path assembler.
      return applyInPlaceTransformWithMutations(
        geometryDocument,
        new Set(entityIds),
        (entity) =>
          transformEntityCoords(entity, (p) =>
            translatePoint(p, delta.x, delta.y),
          ),
      );
    }

    case "geometry/copy-entities": {
      const { entityIds, delta } = command;
      const idsToCopy = new Set(entityIds);
      // Clone each selected entity translated by `delta`; the shared helper
      // mints ids + appends the batch in O(N) (T6a). Sources stay in place.
      return cloneSelectionWithMutations(
        geometryDocument,
        idsToCopy,
        (entity) =>
          transformEntityCoords(entity, (p) =>
            translatePoint(p, delta.x, delta.y),
          ),
      );
    }

    // AutoCAD COPY (base point + second point). Same underlying
    // geometry as `geometry/copy-entities` but payloads the explicit
    // anchor pair so audit-log replay can reconstruct the command
    // without reading live state. Delta is derived server-side here
    // so the pure `executeCopy` in the command-engine matches
    // bit-for-bit with what the reducer does on the document.
    case "geometry/copy": {
      const { sourceEntityIds, basePoint, secondPoint } = command;
      const dx = secondPoint.x - basePoint.x;
      const dy = secondPoint.y - basePoint.y;
      const idsToCopy = new Set(sourceEntityIds);
      // Same clone-by-translation as `copy-entities`, delta derived from the
      // base/second anchor pair. Shared O(N) batch helper (T6a).
      return cloneSelectionWithMutations(
        geometryDocument,
        idsToCopy,
        (entity) =>
          transformEntityCoords(entity, (p) => translatePoint(p, dx, dy)),
      );
    }

    case "geometry/mirror-entities": {
      const { entityIds, mirrorLine1, mirrorLine2, deleteSource } = command;
      const idsToMirror = new Set(entityIds);
      // MIRRTEXT system variable (frozen onto the command at dispatch from
      // the live sysvar store; default 0 = AutoCAD default when absent).
      //   0 → text-bearing fields keep their glyphs READABLE: the anchor
      //       point(s) reflect, but the run is not flipped backwards/
      //       upside-down (mirrorTextReadable for TEXT/MTEXT;
      //       mirrorRotationField's readable branch for the bare-rotation
      //       fields below).
      //   1 → full geometric reflection (rotation 2θ−α [+ group-71 backward
      //       bit / reflected MTEXT direction for TEXT/MTEXT specifically] →
      //       backwards glyphs; mirrorTextFull for TEXT/MTEXT,
      //       mirrorRotationField's full branch for the rest — Codex #342
      //       F1+F2, extended by MIRRTEXT-DIMENSION-MLEADER-ATTRIB).
      // MIRRTEXT-gated fields, beyond TEXT/MTEXT's own `rotation`:
      //   - Dimension.textAngleRelativeToDimLine — DXF group 53, ezdxf's
      //     REAL `text_rotation` (the dimension text's own manual rotation
      //     override, additive to whatever DIMTIH/dimline-derived angle the
      //     renderer would otherwise use — `dimensionGeometry.ts`). GitHub
      //     Codex review PR #558 (post-rebase fresh pass) P1 — the field
      //     this reducer used to mirror, `entity.textRotation` (group 52),
      //     is a DIFFERENT DXF field: per `writer.ts`'s own "ezdxf
      //     acdb_dimension export list" cross-reference, group 52 is
      //     ezdxf's `oblique` (extension-line obliquing), NOT text
      //     rotation, and it also had NO renderer consumer at all — MIRROR
      //     silently computed a value nothing ever read. Redirected to the
      //     field that is both semantically correct (Autodesk DXF ref /
      //     ezdxf) AND now actually wired into the render (see
      //     `DimensionRawFields.textRotationOverride`).
      //   - Multileader.textRotation (MTEXT-content angle, CONTEXT_DATA
      //     MTextData group 42 — only observable when contentType===2/MText;
      //     inert but harmlessly transformed for a BLOCK-content mleader,
      //     matching how MIRRTEXT itself does not affect block content).
      //   - a block-attribute's WORLD `overrideRotation` (EATTEDIT absolute
      //     rotation override — `blockInsertAttributeSchema` doc comment).
      //     A BLOCK-LOCAL (un-overridden) attribute is NOT gated here: it
      //     rides the parent INSERT's own affine, which this reducer does not
      //     yet mirror (negative-scale block-reference representation is a
      //     separate, pre-existing gap — see the "Block" case in
      //     `transformEntityCoords`, not introduced or masked by this fix).
      // Every OTHER entity type is unaffected by MIRRTEXT and always uses the
      // plain coordinate mirror.
      const mirrtext = command.mirrtext ?? 0;
      // GitHub Codex review PR #558 round 2 (round-7 local read-only
      // follow-up) P2 — a schema-valid but near-zero-length mirror axis
      // (below `MIRROR_AXIS_DEGENERATE_LENSQ`) makes `mirrorPoint`/every
      // bare-rotation-field mirror helper a GEOMETRIC no-op — but the
      // Dimension/Multileader/block-attribute HANDEDNESS toggles below
      // previously checked ONLY `mirrtext === 1`, so a degenerate axis
      // could still flip glyph handedness with NO geometric change to
      // justify it — and `computeInverse` returns `null` at the identical
      // threshold (`geometryMutations.ts`), so the resulting glyph-only
      // flip could never be undone either. Computed ONCE (loop-invariant
      // — `mirrorLine1`/`mirrorLine2` don't change per-entity) and
      // ANDed into every `mirrtext === 1` handedness gate below, matching
      // the SAME degeneracy condition the geometric mirror itself uses.
      const mirrorAxisDegenerate = isDegenerateMirrorAxis(mirrorLine1, mirrorLine2);
      // GitHub Codex review PR #558 round 2 (round-17 local read-only
      // follow-up P2) — the mirror axis angle, computed ONCE (loop-
      // invariant, same reasoning as `mirrorAxisDegenerate` above) for
      // `reflectExtrusion`'s own MLeader CONTEXT_DATA `mTextData.extrusion`
      // handling (primary + secondaryContexts) below. Meaningless when the
      // axis is degenerate — every call site below already gates on
      // `!mirrorAxisDegenerate` first.
      const mirrorAxisAngleDeg = mirrorAxisAngleDegFn(mirrorLine1, mirrorLine2);
      // UNDO restore map (entityId → original rotation deg). computeInverse
      // populates this for the deleteSource=true case in BOTH MIRRTEXT modes
      // because the readable-rotation re-derivation is NOT always
      // self-inverse — the position re-mirror is exact, but the rotation
      // must be set back to its captured original. MIRRTEXT=0: the
      // readable-rotation re-derivation flips backward-facing text 180° off
      // on undo (same collapse for TEXT/MTEXT `rotation`, Dimension/
      // Multileader `textRotation`, and a block-attribute's
      // `overrideRotation` — they share `mirrorRotationField`'s readable
      // branch). MIRRTEXT=1: only TEXT/MTEXT need this (a custom-direction
      // MTEXT re-reads its angle from the still-custom direction vector on
      // the inverse, leaving the raw `rotation` drifted — Codex #342 round-3
      // FINDING A); the bare-rotation fields' full-reflection branch is
      // ALWAYS self-inverse (no companion vector to drift), so capturing them
      // for MIRRTEXT=1 is a harmless no-op, kept for one uniform code path.
      // Absent on every forward dispatch (user/AI), so this branch is
      // undo-only.
      const restoreRotations = command.restoreRotations;
      // Codex #342 F3: undo-restore map for MTEXT X-axis direction vectors. The
      // mirror recomputes `xAxisDirection`, which is not always self-inverse —
      // restore the captured original AFTER the coordinate re-mirror for a
      // bit-exact undo of the direction vector.
      const restoreXAxisDirs = command.restoreXAxisDirs;
      // Codex #342 F3: undo-restore map for MTEXT OCS extrusion handedness. The
      // MIRRTEXT=1 mirror sets a mirrored MTEXT's extrusion to (0,0,−1); the
      // Z-flip is involutive, but a source MTEXT that started WITHOUT an extrusion
      // would otherwise undo to a present (0,0,1) — a spurious group 210. A `null`
      // entry records "the source had no extrusion field" → delete it back to
      // absent. Applied AFTER the coordinate re-mirror for a byte-exact undo.
      const restoreExtrusions = command.restoreExtrusions;
      // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — undo restore map for a Block
      // entity's per-attribute WORLD `overrideRotation` values (entityId →
      // { [attribute array index]: original overrideRotation }). Same
      // not-self-inverse story as `restoreRotations` (MIRRTEXT=0 readable
      // collapse), but keyed per-attribute-index rather than one scalar per
      // entity since a Block carries an ARRAY of attributes.
      const restoreAttributeRotations = command.restoreAttributeRotations;
      // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2)
      // — undo restore map for a Multileader's per-annotative-context
      // `textRotation` (entityId → { [annotativeContexts array index]:
      // original textRotation }). Same not-self-inverse story as
      // `restoreRotations`; `mleaderContextDataSchema.textRotation` is
      // `.default(0)` (never optional), so every entry is always a real
      // number — no null-sentinel needed, unlike `restoreAttributeRotations`.
      const restoreContextRotations = command.restoreContextRotations;
      // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2,
      // round 4) — undo restore map for a Multileader's per-scale SECONDARY
      // CONTEXT_DATA blocks' `context.mTextData.rotation` (entityId →
      // { [secondaryContexts array index]: original rotation, or `null` when
      // the source block's MTextData had no rotation field at all).
      // `mleaderMTextDataSchema.rotation` is `.optional()` (never
      // `.default()`) — null-sentinel needed, unlike `restoreContextRotations`
      // above.
      const restoreSecondaryContextRotations =
        command.restoreSecondaryContextRotations;
      // GitHub Codex review PR #558 round 2 P2 — undo restore map for a
      // Multileader's per-scale SECONDARY CONTEXT_DATA blocks'
      // `context.mTextData.extrusion` (entityId → { [secondaryContexts
      // array index]: original extrusion, or `null` when the source block's
      // MTextData had no extrusion field at all). Same index-keyed /
      // null-sentinel shape as `restoreSecondaryContextRotations` above —
      // without this, MIRRTEXT=1 canonicalizing an explicit/tilted
      // `mTextData.extrusion` to `{0,0,-1}` (or deleting it) was NEVER
      // captured, so Undo either flipped a valid `{0,0,1}` to `{0,0,-1}` or
      // permanently collapsed a tilted normal — the document/DXF save no
      // longer matched the pre-command state.
      const restoreSecondaryContextExtrusions =
        command.restoreSecondaryContextExtrusions;
      // GitHub Codex review PR #558 round 2 (round-20 local read-only
      // follow-up) P2 — undo restore map for the PRIMARY (not secondary)
      // context's `contextData.mTextData.extrusion` — see the command-field
      // doc (`packages/contracts/src/editor.ts`) for the full presence-
      // ambiguity rationale.
      const restorePrimaryContextExtrusion =
        command.restorePrimaryContextExtrusion;
      // GitHub Codex review PR #558 (formal bot review, HEAD 4fefb166) P2
      // — undo restore maps for MLeader `mTextData.textDirection` (both
      // secondary and primary contexts) — see the command-field doc
      // (`packages/contracts/src/editor.ts`) for the full presence-
      // ambiguity + floating-point-drift rationale.
      const restoreSecondaryContextTextDirections =
        command.restoreSecondaryContextTextDirections;
      const restorePrimaryContextTextDirection =
        command.restorePrimaryContextTextDirection;
      // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — undo
      // restore map for a Block's per-attribute WORLD
      // `overrideGenerationFlags` (handedness companion to
      // `restoreAttributeRotations`, same null-sentinel shape).
      const restoreAttributeGenerationFlags =
        command.restoreAttributeGenerationFlags;
      // GitHub Codex review PR #558 P2 — undo restore map for a
      // Multileader's entity-level `textMirrored` (entityId → original
      // value, `null` = the source had no field at all — same rationale as
      // `restoreSecondaryContextRotations`'s null-sentinel).
      const restoreTextMirrored = command.restoreTextMirrored;
      // The reflection this command applies, as ONE function — the same affine
      // `transformEntityCoords` is handed below and the same one
      // `geometryShellReducer.ts` probes to raise its RU+EN audit rows.
      const mirrorFn = (p: { x: number; y: number }) =>
        mirrorPoint(p, mirrorLine1, mirrorLine2);
      const mirrorEntity = (entity: DrawingEntity): DrawingEntity | null => {
        // REFUSAL, ANNOUNCED TO THE CALLER (@codex PR #785 round 16,
        // FINDING 2). `transformEntityCoords` declines two families under a
        // reflection and signals it by returning the SAME object: an
        // `ACAD_TABLE` under ANY reflection (`isTableMirrorRefused` — an
        // insertion point plus a horizontal-direction vector cannot name a
        // reflection, only the 180° TURN that vector would be read as), and an
        // OLE2FRAME under a map that does not keep its frame axis-aligned
        // (`isOleFrameTransformRefused` — two opposite corners and no rotation
        // field). Those are the WHOLE family a mirror can reach; the third
        // member, `isTableScaleRefused`, takes a scalar factor and belongs to
        // `geometry/scale-entities`, whose own arm already answers `null` the
        // same way.
        //
        // Answering `null` rather than the unchanged entity is what makes the
        // two halves of MIRROR agree with the audit row: keep-source appends
        // NO stacked duplicate, and delete-source records no `updated`
        // mutation (and so bumps no revision) for an entity nothing touched.
        // These are the SAME predicates `geometryShellReducer.ts` summarises
        // for the warning row, so the document can never decline something the
        // shell reports as mirrored, or vice versa.
        if (
          isTableMirrorRefused(entity, mirrorFn) ||
          isOleFrameTransformRefused(entity, mirrorFn)
        ) {
          return null;
        }
        // Text-bearing entities (TEXT / MTEXT) take the full MIRRTEXT-gated
        // path (position + rotation + direction-vector/generation-flags via
        // mirrorTextReadable/mirrorTextFull). Dimension / Multileader / Block
        // take the plain coordinate mirror for their points, THEN a
        // MIRRTEXT-gated adjustment of their bare rotation-only field(s) —
        // see `mirrorRotationField.ts`'s header for why these three don't
        // need the TEXT/MTEXT machinery's extra fields. Every other entity
        // type is untouched by MIRRTEXT.
        let mirrored: DrawingEntity;
        if (entity.type === "Text" || entity.type === "MText") {
          mirrored =
            mirrtext === 1
              ? mirrorTextFull(entity, mirrorLine1, mirrorLine2)
              : mirrorTextReadable(entity, mirrorLine1, mirrorLine2);
        } else {
          // `mirrorFn`, not a second closure spelling the same reflection:
          // the refusal guard above probes THIS function, so handing
          // `transformEntityCoords` anything else would leave two affines that
          // only happen to agree.
          mirrored = transformEntityCoords(entity, mirrorFn);
          if (mirrored.type === "Dimension" && entity.type === "Dimension") {
            // GitHub Codex review PR #558 round 2 P1 (BOTH MIRRTEXT modes,
            // as of the round-3 local read-only follow-up) — need the
            // per-subtype BASE angle before AND after the mirror
            // (`mirrorReadableRelativeAngleField`'s own doc has the full
            // derivation + the exact worked-example proof) — the entity's
            // DIMTIH (via its named DIMSTYLE, `resolveDimtih` above) + subtype
            // nibble don't change across the mirror, only the extension-line
            // points do, so `nibble`/`dimtih` are computed once and reused for
            // both the pre-mirror (`entity`) and post-mirror (`mirrored`)
            // base-angle calls.
            const nibble = dimensionSubtypeNibble(entity.dimensionType);
            const dimtih = resolveDimtih(geometryDocument, entity.styleName);
            const baseBeforeMirrorDeg = dimensionSubtypeBaseAngleDeg(
              nibble,
              dimtih,
              entity.extLine1Point,
              entity.extLine2Point,
            );
            const baseAfterMirrorDeg = dimensionSubtypeBaseAngleDeg(
              nibble,
              dimtih,
              mirrored.extLine1Point,
              mirrored.extLine2Point,
            );
            mirrored = {
              ...mirrored,
              // GitHub Codex review PR #558 P1 — the real DXF group-53
              // `text_rotation` field (see the case-header doc above), now
              // also threaded into the renderer
              // (`DimensionRawFields.textRotationOverride`).
              //
              // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
              // `textAngleRelativeToDimLine` is ADDITIVE (the renderer adds
              // it on top of the DIMTIH/dimline-derived base angle,
              // `dimensionGeometry.ts`), NOT an absolute angle like
              // Multileader/ATTRIB's bare-rotation fields. MIRRTEXT=1 uses
              // `mirrorFullReflectRelativeAngleField` (round 2 P1, round-3
              // local read-only follow-up fix — the PREVIOUS
              // `mirrorRelativeAngleField` base-INDEPENDENT `−offset`
              // negation only reflects correctly when `base` itself
              // reflects as `2α−base`, true ONLY for Aligned+DIMTIH=false;
              // every other subtype's fixed `base=0` needs the axis's own
              // contribution, which that shortcut silently dropped). MIRRTEXT=0
              // uses `mirrorReadableRelativeAngleField` (round 2 P1 fix — the
              // PREVIOUS `mirrorRotationField` treatment wrongly ran the
              // readable-angle rule on the OFFSET alone, double-counting/
              // ignoring the base's own contribution to whether the FINAL
              // composed angle reads backward; see that function's doc for
              // the composition proof + worked example). Both branches now
              // share the SAME base-before/base-after derivation above —
              // only the reflection RULE (full vs. readable) differs.
              textAngleRelativeToDimLine:
                mirrtext === 1
                  ? mirrorFullReflectRelativeAngleField(
                      mirrored.textAngleRelativeToDimLine,
                      baseBeforeMirrorDeg,
                      baseAfterMirrorDeg,
                      mirrorLine1,
                      mirrorLine2,
                    )
                  : mirrorReadableRelativeAngleField(
                      mirrored.textAngleRelativeToDimLine,
                      baseBeforeMirrorDeg,
                      baseAfterMirrorDeg,
                      mirrorLine1,
                      mirrorLine2,
                    ),
              // GitHub Codex review PR #558 P1 — handedness companion,
              // matching Multileader/ATTRIB's convention (XOR-toggled ONLY
              // for MIRRTEXT=1; readable mode never flips glyphs). See
              // `DimensionEntity.textMirrored`'s doc for the DXF-persistence
              // scope note.
              textMirrored:
                mirrtext === 1 && !mirrorAxisDegenerate
                  ? !mirrored.textMirrored
                  : mirrored.textMirrored,
            };
          } else if (mirrored.type === "Multileader") {
            // GitHub Codex review PR #558 (formal bot review, HEAD
            // a3e09987) P2 — "Skip text transforms for contentless
            // multileaders". `textRotation`/`textMirrored`/
            // `annotativeContexts[].textRotation` are ALL text-orientation
            // fields with no meaning outside MText content — the renderer's
            // own `multileaderGeometry.ts` only ever builds text geometry
            // for `contentType === 2` (`raw.contentType === 2`), never for
            // `contentType === 1` (Block, which uses the wholly separate
            // `blockRotation` field) or `contentType === 0` (no content —
            // a real, DXF-valid state, AutoCAD's own leaderless/label-less
            // MLEADER). Touching these fields for a non-MText multileader
            // was not just inert: `textMirrored` toggling is ONE of the OR
            // conditions gating `needsMaterializedSpacingMText`
            // (`dxfBridge.mleaderContextConversion.ts`) — mirroring a
            // Block-content or content-less multileader could MATERIALIZE
            // a spurious MTextData block on export for an entity that has
            // no text content at all. Gated on `contentType === 2` to
            // match the renderer's own precise convention exactly.
            //
            // GitHub Codex review PR #558 (formal bot review, HEAD
            // 44f4f9b8) P2 — "Gate context MText transforms on MLeader
            // content type". round-33's own comment claimed the
            // secondaryContexts/primary-contextData-extrusion handling
            // further down "naturally lacks or leaves empty" `mTextData`
            // for a non-MText multileader — WRONG, disproven by the DXF
            // schema itself: `hasMText`/`mTextData` (group 290, INSIDE each
            // CONTEXT_DATA block) is a field COMPLETELY INDEPENDENT of the
            // entity-level `contentType` (group 172) — this codebase's own
            // parser/writer already accept and round-trip a MLeader whose
            // BODY is Block/content-less (172 = 1/0) while its CONTEXT_DATA
            // still carries a preserved/cached inline MTextData sub-block
            // (`hasMText=1`) — real AutoCAD retains this kind of data across
            // a content-type toggle. `isMTextContentType` (computed once,
            // reused by every text-orientation gate in this branch) now
            // ALSO gates the secondaryContexts and primary-context
            // extrusion/rotation handling below, so a non-MText multileader
            // leaves its preserved-but-currently-inactive MText data
            // byte-identical to source too — same "don't invent/rewrite
            // data the active content type doesn't use" reasoning as the
            // flat-field gate above, chosen over the alternative (mirror
            // the cached data anyway, in case the user later toggles back
            // to MText) for lack of a validated AutoCAD reference to check
            // that alternative against — the conservative, preserve-
            // source-never-infer choice this PR has used throughout.
            //
            // `?? 2` — `multileaderEntitySchema.contentType` is `.default(2)`,
            // never `.optional()`, so a SCHEMA-PARSED entity always carries
            // an explicit value; a hand-built test/regression-harness
            // literal (not `.parse()`-d) can genuinely omit it, and per the
            // schema's own default that omission means "MText" — same
            // `?? []`-class guard `scaleEntityRadii.ts` already documents
            // for an analogous minimal-literal gap.
            const isMTextContentType = (mirrored.contentType ?? 2) === 2;
            if (isMTextContentType) {
              mirrored = {
                ...mirrored,
                // GitHub Codex review PR #558 (post-rebase fresh pass) P1 —
                // MULTILEADER's `textRotation` (and its CONTEXT_DATA siblings
                // below) are stored in RADIANS end-to-end (model ⇔ DXF group
                // 42, no unit conversion at either boundary — same anomaly as
                // real MTEXT's own group-50 field; see `mirrorRotationField
                // Radians`'s doc for the two independent confirmations). Every
                // OTHER bare-rotation field this reducer mirrors (Dimension /
                // block-ATTRIB) is decimal DEGREES, matching `mirrorRotation
                // Field`'s own convention — so ONLY the Multileader branch
                // needs the radians-aware wrapper.
                textRotation: mirrorRotationFieldRadians(
                  mirrored.textRotation,
                  mirrorLine1,
                  mirrorLine2,
                  mirrtext,
                ),
                // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
                // handedness companion to `textRotation` above. XOR-toggled
                // ONLY for MIRRTEXT=1 (full geometric reflection) — MIRRTEXT=0
                // (readable) re-derives a forward-reading ANGLE but never
                // flips glyphs, exactly matching TEXT/MTEXT's own convention
                // (`mirrorTextReadable` never touches `generationFlags`/
                // `extrusion`; only `mirrorTextFull` does). A boolean XOR is
                // its own inverse — no undo-restore snapshot needed (unlike
                // `textRotation`'s readable-mode collapse), matching how
                // TEXT's own `generationFlags` XOR needs no restore map either.
                textMirrored:
                  mirrtext === 1 && !mirrorAxisDegenerate
                    ? !mirrored.textMirrored
                    : mirrored.textMirrored,
                // local Codex read-only review (P2) — the render (`multileader
                // FromEntity.ts`) PREFERS an active annotative context's OWN
                // `textRotation` over the flat field above
                // (`annoCtx?.textRotation ?? entity.textRotation`), so an
                // annotative MLeader's per-scale snapshots need the SAME
                // MIRRTEXT-gated transform or the render/export stays on the
                // stale angle whenever a context is active. `mleaderContextData
                // Schema.textRotation` is `.default(0)` (never optional), so
                // every entry always carries a real number — no null-sentinel
                // needed, unlike the Block-attribute case below. Same radians
                // unit as the flat field above (one CONTEXT_DATA MTextData
                // group-42 home either way).
                annotativeContexts: mirrored.annotativeContexts.map((ctx) => ({
                  ...ctx,
                  textRotation: mirrorRotationFieldRadians(
                    ctx.textRotation,
                    mirrorLine1,
                    mirrorLine2,
                    mirrtext,
                  ),
                })),
              };
            }
            // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only
            // review P2, round 4) — an IMPORTED annotative Multileader's
            // per-scale SECONDARY CONTEXT_DATA blocks also carry their own
            // `context.mTextData.rotation` (group 42), and the export
            // bridge (`modelMLeaderSecondaryContextsToDxf`) re-emits every
            // secondary block VERBATIM — by design for content/height/
            // landing-gap (those are legitimately per-scale authored
            // values), but rotation is different: MIRROR is a geometric
            // transform of the WHOLE entity, so every scale's text angle
            // must stay consistent, not just the primary/annotativeContexts
            // ones above. `presencePreservingRotation` applies the SAME
            // byte-faithfulness rule as `withWorldAttributeRotation`
            // (`mTextData.rotation` is `.optional()`, never `.default()` —
            // absence must stay absent unless the mirrored value actually
            // diverges from the implicit 0 default). Guarded on the whole
            // array being present so an entity with NO secondary blocks
            // never gains a spurious `secondaryContexts: undefined` key.
            if (isMTextContentType && mirrored.secondaryContexts) {
              mirrored = {
                ...mirrored,
                secondaryContexts: mirrored.secondaryContexts.map((sec) => {
                  const mTextData = sec.context.mTextData;
                  if (!mTextData) return sec;
                  const nextRotation = presencePreservingRotation(
                    mTextData.rotation,
                    // GitHub Codex review PR #558 P1 — group-42 MTextData
                    // rotation is the SAME radians field as the flat
                    // `textRotation` above; radians-aware wrapper required.
                    mirrorRotationFieldRadians(
                      mTextData.rotation ?? 0,
                      mirrorLine1,
                      mirrorLine2,
                      mirrtext,
                    ),
                  );
                  let nextMTextData = mTextData;
                  if (nextRotation !== undefined) {
                    nextMTextData = { ...nextMTextData, rotation: nextRotation };
                  }
                  // GitHub Codex review PR #558 (post-rebase fresh pass) P2,
                  // corrected by the round-17 local read-only follow-up P2
                  // — "Reflect secondary MLeader extrusions without
                  // canonicalizing" — an imported annotative MLeader's
                  // SECONDARY blocks also carry their OWN `mTextData
                  // .extrusion` handedness. MIRROR is a geometric transform
                  // of the WHOLE entity, so every scale's glyph handedness
                  // must flip consistently, not just the primary context's
                  // (below) — otherwise a saved/reloaded annotative MLeader
                  // shows the mirrored primary scale next to unmirrored
                  // secondary scales. MIRRTEXT=1 only (readable mode never
                  // flips glyphs). The FIRST draft canonicalised to the
                  // axis-aligned (0,0,±1) pair (correct ONLY for a default/
                  // untilted source) — a genuinely tilted (non-axis-aligned)
                  // imported extrusion lost its original OCS tilt on every
                  // mirror-and-resave. `reflectMLeaderExtrusion` (this
                  // file's own helper, wrapping command-engine's
                  // `reflectExtrusion` — the SAME general reflection real
                  // MTEXT's own `mirrorTextFull` uses) properly reflects
                  // the FULL vector, presence-preserving (omits when it
                  // reduces to the exact canonical default) — see that
                  // helper's own doc for the full byte-faithfulness
                  // rationale. Involutive (a second mirror across the SAME
                  // axis restores the original), so no undo-restore
                  // snapshot is needed either.
                  if (mirrtext === 1 && !mirrorAxisDegenerate) {
                    const nextExtrusion = reflectMLeaderExtrusion(
                      mTextData.extrusion,
                      mirrorAxisAngleDeg,
                    );
                    if (nextExtrusion === undefined) {
                      const { extrusion: _drop, ...withoutExtrusion } =
                        nextMTextData;
                      void _drop;
                      nextMTextData = withoutExtrusion;
                    } else {
                      nextMTextData = {
                        ...nextMTextData,
                        extrusion: nextExtrusion,
                      };
                    }
                  }
                  // GitHub Codex review PR #558 (formal bot review, HEAD
                  // 2d0dd928) P2 — "Mirror MLeader text-direction vectors
                  // with the rotation". `mTextData.textDirection` (DXF
                  // group 13/23/33, `.optional()` in model.ts) is
                  // ezdxf's own "'horizontal' text direction vector...
                  // higher priority than rotation when both present" —
                  // the SAME precedence relationship standalone MTEXT's
                  // own `xAxisDirection` has over its `rotation` (see
                  // `reflectVectorXY`'s own doc, `geometryTransforms.ts`,
                  // exported specifically for this reuse). Left
                  // completely untouched before the round with HEAD
                  // 2d0dd928, so a reader honoring textDirection over
                  // rotation would see the STALE pre-mirror orientation.
                  //
                  // GitHub Codex review PR #558 round 38 (formal bot review,
                  // HEAD ecca1c8d) P2 — "Mirror MLeader direction vectors in
                  // readable mode". The 2d0dd928 round above ONLY covered
                  // FULL-mirror (mirrtext===1, ezdxf's documented
                  // `transform_direction` formula) and explicitly deferred
                  // READABLE mode (mirrtext===0) as having "no established
                  // reference to validate against" — that reference exists
                  // in this codebase already (`mirrorTextReadable`,
                  // standalone MTEXT's own readable-mirror handler; see
                  // `reflectMLeaderTextDirectionReadable`'s own doc, this
                  // file, for the full derivation). Un-gated from `mirrtext
                  // === 1` — now runs for BOTH mirror modes, branching
                  // internally on which formula applies, still gated on
                  // `!mirrorAxisDegenerate` (an ill-defined axis angle skips
                  // both, same invariant every other handedness field in
                  // this reducer honours).
                  if (!mirrorAxisDegenerate && mTextData.textDirection) {
                    nextMTextData = {
                      ...nextMTextData,
                      textDirection:
                        mirrtext === 1
                          ? reflectVectorXY(
                              mTextData.textDirection,
                              mirrorAxisAngleDeg,
                            )
                          : reflectMLeaderTextDirectionReadable(
                              mTextData.textDirection,
                              mirrorAxisAngleDeg,
                            ),
                    };
                  }
                  if (nextMTextData === mTextData) return sec;
                  return {
                    ...sec,
                    context: {
                      ...sec.context,
                      mTextData: nextMTextData,
                    },
                  };
                }),
              };
            }
            // NOTE — tracked as https://github.com/cosc/Edit-dwg/issues/562
            // ("Multileader secondaryContexts position not updated by any
            // transform command") — see that issue for the full root-cause
            // writeup and completion plan. Short version: this does NOT
            // make MIRROR (or MOVE/COPY/ROTATE/SCALE, which share this same
            // gap) of an annotative Multileader's SECONDARY contexts fully
            // AutoCAD-faithful: `sec.context
            // .mTextData.insert` (the per-scale text anchor, group 12/22/32)
            // and `sec.leaderRoots` (that block's OWN leader-line vertices —
            // "This block's OWN leader hierarchy", `model.ts`
            // `mleaderSecondaryContextSchema`) are still untouched by this
            // reducer's geometry transform. `transformEntityCoords`
            // (`geometry-transforms.ts`, shared by every MOVE/COPY/ROTATE
            // /SCALE/MIRROR command) has NEVER walked into `contextData` or
            // `secondaryContexts` at all — confirmed by direct inspection,
            // not assumed — and `modelMLeaderSecondaryContextsToDxf`
            // (`dxfBridge.mleaderContextConversion.ts`) re-emits every
            // secondary block VERBATIM by explicit design (its own doc
            // comment: "NO entity-level UI edits are projected... preserve-
            // source-never-infer"), unlike the PRIMARY context whose
            // `mTextData.insert` IS re-derived from `edits.textLocation` on
            // divergence at EXPORT time (`modelMLeaderContextToDxf`, same
            // file) — there is no equivalent per-scale projection for
            // secondaries. Net effect: after ANY transform of an annotative
            // Multileader, its alternate-scale (secondary) representations
            // keep their ORIGINAL anchor/leader geometry regardless of
            // whether this PR's rotation/extrusion sync above ran — this is
            // a separate, pre-existing, ORTHOGONAL gap (geometry position,
            // not text orientation/handedness) that predates every round of
            // this PR and is NOT introduced or masked by it: before this
            // PR's rotation/extrusion sync existed, a mirrored secondary
            // block was ALSO stuck at its stale position with STALE
            // orientation; after, it is stuck at the SAME stale position
            // with CORRECT orientation — this PR's own scope (MIRRTEXT text
            // handedness) is satisfied either way. Fixing secondary-context
            // POSITION transform is a materially larger, separate feature
            // (a `transformEntityCoords` change affecting every transform
            // command, not just MIRROR) tracked as a follow-up, not folded
            // into this PR silently.
            // GitHub Codex review PR #558 round 2 (round-17 local read-only
            // follow-up) P2 — "Preserve the primary MLeader extrusion when
            // mirroring". The PRIMARY CONTEXT_DATA block's own `mTextData
            // .extrusion` (`mirrored.contextData`, the byte-faithful RAW
            // structure preserved from import — DISTINCT from
            // `annotativeContexts`/`secondaryContexts` above) was
            // previously left completely UNTOUCHED by this reducer: only
            // the flat `textMirrored` SUMMARY boolean got XOR-toggled
            // above, so the DXF export bridge
            // (`dxfBridge.mleaderContextConversion.ts modelMLeaderContext
            // ToDxf`) — which has NO access to the mirror axis, only the
            // boolean — had to canonicalise a divergent extrusion to the
            // axis-aligned `(0,0,±1)` pair on export, losing a genuinely
            // tilted (imported) OCS normal. Reflecting it HERE, where the
            // mirror axis IS available, keeps `contextData` geometrically
            // consistent with `textMirrored` at all times — so the export
            // bridge's own divergence check never needs to canonicalise
            // for a reducer-driven MIRROR (the ONLY way `textMirrored`
            // changes in this codebase). Same `reflectMLeaderExtrusion`
            // helper, same presence-preserving/involutive rationale as the
            // secondaryContexts case above.
            if (
              isMTextContentType &&
              !mirrorAxisDegenerate &&
              mirrored.contextData?.mTextData
            ) {
              const mTextData = mirrored.contextData.mTextData;
              let nextMTextData = mTextData;
              if (mirrtext === 1) {
                const nextExtrusion = reflectMLeaderExtrusion(
                  mTextData.extrusion,
                  mirrorAxisAngleDeg,
                );
                if (nextExtrusion === undefined) {
                  const { extrusion: _drop, ...withoutExtrusion } = mTextData;
                  void _drop;
                  nextMTextData = withoutExtrusion;
                } else {
                  nextMTextData = {
                    ...nextMTextData,
                    extrusion: nextExtrusion,
                  };
                }
              }
              // GitHub Codex review PR #558 (formal bot review, HEAD
              // 2d0dd928) P2 — "Mirror MLeader text-direction vectors with
              // the rotation", primary-context twin of the secondaryContexts
              // fix above — see that block's own doc for the full
              // ezdxf-precedence rationale.
              //
              // GitHub Codex review PR #558 round 38 (formal bot review,
              // HEAD ecca1c8d) P2 — "Mirror MLeader direction vectors in
              // readable mode", primary-context twin of the
              // secondaryContexts round-38 fix above — same
              // `reflectMLeaderTextDirectionReadable` helper, same
              // `mirrorTextReadable` precedent, un-gated from `mirrtext ===
              // 1` for the SAME reason (outer `if` above dropped that
              // restriction too, so `mirrtext === 0` reaches this branch).
              if (mTextData.textDirection) {
                nextMTextData = {
                  ...nextMTextData,
                  textDirection:
                    mirrtext === 1
                      ? reflectVectorXY(
                          mTextData.textDirection,
                          mirrorAxisAngleDeg,
                        )
                      : reflectMLeaderTextDirectionReadable(
                          mTextData.textDirection,
                          mirrorAxisAngleDeg,
                        ),
                };
              }
              if (nextMTextData !== mTextData) {
                mirrored = {
                  ...mirrored,
                  contextData: {
                    ...mirrored.contextData,
                    mTextData: nextMTextData,
                  },
                };
              }
            }
          } else if (mirrored.type === "Block" && mirrored.attributes.length > 0) {
            mirrored = {
              ...mirrored,
              // Gate on `overridePosition`, NOT `overrideRotation` (local
              // Codex read-only review P1) — see `withWorldAttributeRotation`'s
              // doc for the full regime rationale; a block-local attribute's
              // `overrideRotation` (present with no position override) rides
              // the INSERT's own affine and must stay untouched here.
              attributes: mirrored.attributes.map((attr) => {
                if (attr.overridePosition === undefined) return attr;
                const rotated = withWorldAttributeRotation(
                  attr,
                  mirrorRotationField(
                    attr.overrideRotation ?? 0,
                    mirrorLine1,
                    mirrorLine2,
                    mirrtext,
                  ),
                );
                // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
                // handedness companion to the rotation above, using the
                // SAME uniform bit-4 (UPSIDE_DOWN) XOR real TEXT's own
                // `mirrorTextFull` applies for EVERY axis (`geometryTransforms
                // .ts`'s "UNIFORM (geometrically-exact) rule" doc) — a WORLD
                // block-attribute is bare-angle TEXT in every other respect
                // (`mirrorRotationField`'s header). ONLY for MIRRTEXT=1 (full
                // reflection); MIRRTEXT=0 (readable) never flips glyphs,
                // matching TEXT/MTEXT/Multileader's own convention. XOR is
                // its own inverse — no undo-restore snapshot needed.
                return mirrtext === 1 && !mirrorAxisDegenerate
                  ? withWorldAttributeGenerationFlags(
                      rotated,
                      (rotated.overrideGenerationFlags ?? 0) ^
                        TEXT_GEN_FLAG_UPSIDE_DOWN,
                    )
                  : rotated;
              }),
            };
          }
        }
        // Undo of a text mirror (either MIRRTEXT mode): position was re-mirrored
        // exactly by the line above; overwrite the rotation with the captured
        // original so text-baseline orientation round-trips to the bit (only
        // Text/MText carry a `rotation` field and only those are listed in the
        // map). Drives MIRRTEXT=0 readable undo AND MIRRTEXT=1 custom-dir MTEXT
        // undo (FINDING A) — the latter would otherwise leave a drifted rotation.
        if (
          restoreRotations &&
          Object.prototype.hasOwnProperty.call(restoreRotations, entity.id) &&
          (mirrored.type === "Text" || mirrored.type === "MText")
        ) {
          let restored: DrawingEntity = {
            ...mirrored,
            rotation: restoreRotations[entity.id]!,
          };
          // Restore the original MTEXT direction vector too (when captured) so
          // the un-reflected group 11/21/31 round-trips exactly on undo.
          if (
            restored.type === "MText" &&
            restoreXAxisDirs &&
            Object.prototype.hasOwnProperty.call(restoreXAxisDirs, entity.id)
          ) {
            restored = {
              ...restored,
              xAxisDirection: restoreXAxisDirs[entity.id]!,
            };
          }
          // Codex #342 F3: restore the original MTEXT extrusion handedness. A
          // captured `null` means the source had NO extrusion field — strip it
          // back to absent (omit the key) rather than re-emit (0,0,1); a captured
          // vector is reinstated verbatim. Only MTEXT carries this map.
          if (
            restored.type === "MText" &&
            restoreExtrusions &&
            Object.prototype.hasOwnProperty.call(restoreExtrusions, entity.id)
          ) {
            const captured = restoreExtrusions[entity.id];
            if (captured === null) {
              const { extrusion: _drop, ...withoutExtrusion } = restored;
              void _drop;
              restored = withoutExtrusion as DrawingEntity;
            } else if (captured) {
              restored = { ...restored, extrusion: captured };
            }
          }
          return restored;
        }
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — undo restore for Dimension's
        // bare `textAngleRelativeToDimLine` (DXF group 53, same not-self-
        // inverse readable collapse as TEXT/MTEXT `rotation`, restored via
        // the SAME `restoreRotations` map — only the target FIELD NAME
        // differs). GitHub Codex review PR #558 P1 — field renamed from the
        // group-52 `textRotation` (see the case-header doc above).
        if (mirrored.type === "Dimension") {
          let restoredDim = mirrored;
          if (
            restoreRotations &&
            Object.prototype.hasOwnProperty.call(restoreRotations, entity.id)
          ) {
            restoredDim = {
              ...restoredDim,
              textAngleRelativeToDimLine: restoreRotations[entity.id]!,
            };
          }
          // GitHub Codex review PR #558 (post-rebase fresh pass) P1 — undo
          // restore for the handedness companion, same null-sentinel
          // convention (absent stays absent, never an explicit `false`) as
          // the Multileader case below — this Dimension branch and that
          // one share the SAME `restoreTextMirrored` map (entityId-keyed;
          // ids never collide across entity types).
          if (
            restoreTextMirrored &&
            Object.prototype.hasOwnProperty.call(
              restoreTextMirrored,
              entity.id,
            )
          ) {
            const captured = restoreTextMirrored[entity.id];
            if (captured === null) {
              const { textMirrored: _drop, ...withoutMirrored } = restoredDim;
              void _drop;
              restoredDim = withoutMirrored as typeof restoredDim;
            } else {
              restoredDim = { ...restoredDim, textMirrored: captured };
            }
          }
          if (restoredDim !== mirrored) return restoredDim;
        }
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex review P2) — undo
        // restore for Multileader's bare `textRotation` AND its per-
        // annotative-context `textRotation` entries (index-keyed; a mirror
        // command never adds/removes contexts, so the index lines up).
        if (mirrored.type === "Multileader") {
          let restoredMLeader = mirrored;
          if (
            restoreRotations &&
            Object.prototype.hasOwnProperty.call(restoreRotations, entity.id)
          ) {
            restoredMLeader = {
              ...restoredMLeader,
              textRotation: restoreRotations[entity.id]!,
            };
          }
          // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — undo
          // restore for the handedness companion. A captured `null` means
          // the source had no `textMirrored` field at all — restore back to
          // absent (omit the key), not an explicit `false`.
          if (
            restoreTextMirrored &&
            Object.prototype.hasOwnProperty.call(
              restoreTextMirrored,
              entity.id,
            )
          ) {
            const captured = restoreTextMirrored[entity.id];
            if (captured === null) {
              const { textMirrored: _drop, ...withoutMirrored } =
                restoredMLeader;
              void _drop;
              restoredMLeader = withoutMirrored as typeof restoredMLeader;
            } else {
              restoredMLeader = { ...restoredMLeader, textMirrored: captured };
            }
          }
          if (
            restoreContextRotations &&
            Object.prototype.hasOwnProperty.call(
              restoreContextRotations,
              entity.id,
            )
          ) {
            const perContext = restoreContextRotations[entity.id]!;
            restoredMLeader = {
              ...restoredMLeader,
              annotativeContexts: restoredMLeader.annotativeContexts.map(
                (ctx, index) => {
                  const key = String(index);
                  return Object.prototype.hasOwnProperty.call(
                    perContext,
                    key,
                  )
                    ? { ...ctx, textRotation: perContext[key]! }
                    : ctx;
                },
              ),
            };
          }
          // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review
          // P2, round 4) — undo restore for a Multileader's per-scale
          // SECONDARY CONTEXT_DATA blocks' `context.mTextData.rotation`
          // (index-keyed; a mirror command never adds/removes secondary
          // blocks, so the index lines up). A captured `null` means the
          // source block's MTextData had NO rotation field at all — restore
          // it back to absent (delete the key), never an emitted `0`, same
          // rationale as `withWorldAttributeRotation`/
          // `presencePreservingRotation`.
          if (
            restoreSecondaryContextRotations &&
            Object.prototype.hasOwnProperty.call(
              restoreSecondaryContextRotations,
              entity.id,
            ) &&
            restoredMLeader.secondaryContexts
          ) {
            const perSecondary = restoreSecondaryContextRotations[entity.id]!;
            restoredMLeader = {
              ...restoredMLeader,
              secondaryContexts: restoredMLeader.secondaryContexts.map(
                (sec, index) => {
                  const key = String(index);
                  if (
                    !Object.prototype.hasOwnProperty.call(perSecondary, key) ||
                    !sec.context.mTextData
                  ) {
                    return sec;
                  }
                  const captured = perSecondary[key];
                  if (captured === null) {
                    const { rotation: _drop, ...restMTextData } =
                      sec.context.mTextData;
                    void _drop;
                    return {
                      ...sec,
                      context: { ...sec.context, mTextData: restMTextData },
                    };
                  }
                  return {
                    ...sec,
                    context: {
                      ...sec.context,
                      mTextData: {
                        ...sec.context.mTextData,
                        rotation: captured,
                      },
                    },
                  };
                },
              ),
            };
          }
          // GitHub Codex review PR #558 round 2 P2 — undo restore for the
          // SAME secondary blocks' `context.mTextData.extrusion` (see the
          // command-field doc above). Applied as its OWN pass (not folded
          // into the rotation pass above) since a captured entry can exist
          // independent of whether rotation also changed — a block whose
          // ONLY MIRRTEXT=1-driven change was the extrusion handedness
          // flip still needs its own restore entry honoured.
          if (
            restoreSecondaryContextExtrusions &&
            Object.prototype.hasOwnProperty.call(
              restoreSecondaryContextExtrusions,
              entity.id,
            ) &&
            restoredMLeader.secondaryContexts
          ) {
            const perSecondary =
              restoreSecondaryContextExtrusions[entity.id]!;
            restoredMLeader = {
              ...restoredMLeader,
              secondaryContexts: restoredMLeader.secondaryContexts.map(
                (sec, index) => {
                  const key = String(index);
                  if (
                    !Object.prototype.hasOwnProperty.call(perSecondary, key) ||
                    !sec.context.mTextData
                  ) {
                    return sec;
                  }
                  const captured = perSecondary[key];
                  if (captured === null) {
                    const { extrusion: _drop, ...restMTextData } =
                      sec.context.mTextData;
                    void _drop;
                    return {
                      ...sec,
                      context: { ...sec.context, mTextData: restMTextData },
                    };
                  }
                  return {
                    ...sec,
                    context: {
                      ...sec.context,
                      mTextData: {
                        ...sec.context.mTextData,
                        extrusion: captured,
                      },
                    },
                  };
                },
              ),
            };
          }
          // GitHub Codex review PR #558 round 2 (round-20 local read-only
          // follow-up) P2 — "Restore primary MLeader extrusion state on
          // undo". Same null-sentinel restore as the secondary-context
          // pass above, applied to the PRIMARY `contextData.mTextData
          // .extrusion` instead — see the command-field doc for the
          // presence-ambiguity this closes (an explicit source `{0,0,1}`
          // must round-trip back to explicit `{0,0,1}`, not OMITTED, even
          // though `reflectMLeaderExtrusion`'s forward pass would
          // otherwise correctly omit a freshly-reflected canonical
          // default).
          if (
            restorePrimaryContextExtrusion &&
            Object.prototype.hasOwnProperty.call(
              restorePrimaryContextExtrusion,
              entity.id,
            ) &&
            restoredMLeader.contextData?.mTextData
          ) {
            const captured = restorePrimaryContextExtrusion[entity.id]!;
            const mTextData = restoredMLeader.contextData.mTextData;
            const nextMTextData =
              captured === null
                ? (() => {
                    const { extrusion: _drop, ...rest } = mTextData;
                    void _drop;
                    return rest;
                  })()
                : { ...mTextData, extrusion: captured };
            restoredMLeader = {
              ...restoredMLeader,
              contextData: {
                ...restoredMLeader.contextData,
                mTextData: nextMTextData,
              },
            };
          }
          // GitHub Codex review PR #558 (formal bot review, HEAD
          // 4fefb166) P2 — "Restore MLeader text directions exactly on
          // undo". Same secondary-context null-sentinel restore pattern as
          // `restoreSecondaryContextExtrusions` above, applied to
          // `context.mTextData.textDirection` instead.
          if (
            restoreSecondaryContextTextDirections &&
            Object.prototype.hasOwnProperty.call(
              restoreSecondaryContextTextDirections,
              entity.id,
            ) &&
            restoredMLeader.secondaryContexts
          ) {
            const perSecondary =
              restoreSecondaryContextTextDirections[entity.id]!;
            restoredMLeader = {
              ...restoredMLeader,
              secondaryContexts: restoredMLeader.secondaryContexts.map(
                (sec, index) => {
                  const key = String(index);
                  if (
                    !Object.prototype.hasOwnProperty.call(perSecondary, key) ||
                    !sec.context.mTextData
                  ) {
                    return sec;
                  }
                  const captured = perSecondary[key];
                  if (captured === null) {
                    const { textDirection: _drop, ...restMTextData } =
                      sec.context.mTextData;
                    void _drop;
                    return {
                      ...sec,
                      context: { ...sec.context, mTextData: restMTextData },
                    };
                  }
                  return {
                    ...sec,
                    context: {
                      ...sec.context,
                      mTextData: {
                        ...sec.context.mTextData,
                        textDirection: captured,
                      },
                    },
                  };
                },
              ),
            };
          }
          // GitHub Codex review PR #558 (formal bot review, HEAD
          // 4fefb166) P2 — primary-context twin of the secondary restore
          // above, for `contextData.mTextData.textDirection`.
          if (
            restorePrimaryContextTextDirection &&
            Object.prototype.hasOwnProperty.call(
              restorePrimaryContextTextDirection,
              entity.id,
            ) &&
            restoredMLeader.contextData?.mTextData
          ) {
            const captured = restorePrimaryContextTextDirection[entity.id]!;
            const mTextData = restoredMLeader.contextData.mTextData;
            const nextMTextData =
              captured === null
                ? (() => {
                    const { textDirection: _drop, ...rest } = mTextData;
                    void _drop;
                    return rest;
                  })()
                : { ...mTextData, textDirection: captured };
            restoredMLeader = {
              ...restoredMLeader,
              contextData: {
                ...restoredMLeader.contextData,
                mTextData: nextMTextData,
              },
            };
          }
          if (restoredMLeader !== mirrored) return restoredMLeader;
        }
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — undo restore for a Block's
        // per-attribute WORLD `overrideRotation` values (index-keyed; a
        // mirror command never adds/removes attributes, so the index into
        // the pre- and post-mutation `attributes` arrays lines up). A
        // captured `null` means the source attribute had NO overrideRotation
        // field at all — restore it back to absent (delete the key), never
        // an emitted `0` (byte-faithful DXF ATTRIB group-50 round-trip, same
        // rationale as `withWorldAttributeRotation`).
        // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — undo
        // restore for the handedness companion, `overrideGenerationFlags`
        // (index-keyed, same null-sentinel convention as `overrideRotation`
        // above). Combined into the SAME `attributes.map` pass as the
        // rotation restore below (both are index-keyed over the identical
        // attribute array) rather than two separate passes.
        const hasRotationRestore =
          restoreAttributeRotations &&
          Object.prototype.hasOwnProperty.call(
            restoreAttributeRotations,
            entity.id,
          );
        const hasFlagsRestore =
          restoreAttributeGenerationFlags &&
          Object.prototype.hasOwnProperty.call(
            restoreAttributeGenerationFlags,
            entity.id,
          );
        if ((hasRotationRestore || hasFlagsRestore) && mirrored.type === "Block") {
          const perAttribute = hasRotationRestore
            ? restoreAttributeRotations[entity.id]!
            : undefined;
          const perAttributeFlags = hasFlagsRestore
            ? restoreAttributeGenerationFlags[entity.id]!
            : undefined;
          return {
            ...mirrored,
            attributes: mirrored.attributes.map((attr, index) => {
              const key = String(index);
              let next = attr;
              if (
                perAttribute &&
                Object.prototype.hasOwnProperty.call(perAttribute, key)
              ) {
                const captured = perAttribute[key];
                if (captured === null) {
                  const { overrideRotation: _drop, ...rest } = next;
                  void _drop;
                  next = rest;
                } else {
                  next = { ...next, overrideRotation: captured };
                }
              }
              if (
                perAttributeFlags &&
                Object.prototype.hasOwnProperty.call(perAttributeFlags, key)
              ) {
                const captured = perAttributeFlags[key];
                if (captured === null) {
                  const { overrideGenerationFlags: _drop, ...rest } = next;
                  void _drop;
                  next = rest;
                } else {
                  next = { ...next, overrideGenerationFlags: captured };
                }
              }
              return next;
            }),
          };
        }
        return mirrored;
      };

      if (deleteSource) {
        // Mirror in-place (erase source = replace with mirrored version).
        // Pure coordinate transform — same safety story as MOVE. T6c: the
        // deleteSource variant adds/removes no entity and changes no
        // id/spaceId, so invariants are preserved by construction → fast-path.
        // (The deleteSource=false variant ADDS mirrored copies and stays on the
        // full assembler via cloneSelectionWithMutations below.)
        // T6d-1: routed through the shared in-place helper (O(touched), no
        // full-document index build). `mirrorEntity` is passed verbatim — it
        // still captures restoreRotations/restoreXAxisDirs/restoreExtrusions
        // and the MIRRTEXT branching unchanged.
        return applyInPlaceTransformWithMutations(
          geometryDocument,
          idsToMirror,
          mirrorEntity,
        );
      }

      // Keep source, add mirrored copies — shared O(N) batch clone helper (T6a).
      return cloneSelectionWithMutations(
        geometryDocument,
        idsToMirror,
        mirrorEntity,
      );
    }

    // ── ROTATE ──────────────────────────────────────────────────────
    case "geometry/rotate-entities": {
      const { entityIds, basePoint, angleDeg } = command;
      // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2,
      // round 4) — undo restore map for a Block entity's per-attribute
      // WORLD `overrideRotation` values (entityId → { [attribute array
      // index]: original overrideRotation }, `null` sentinel = the source
      // attribute had NO overrideRotation field at all). Populated ONLY by
      // `computeInverse`'s "geometry/rotate-entities" case; absent on every
      // forward (user/AI) dispatch, so this block is undo-only. See
      // `withWorldAttributeRotation`'s doc comment for why ROTATE's own
      // angle arithmetic (`+angleDeg` then `-angleDeg`) is exactly
      // self-inverse and yet still needs this snapshot: presence
      // preservation means the blind angle-negation undo alone cannot tell
      // "restore to absent" apart from "restore to an explicit 0".
      const restoreAttributeRotations = command.restoreAttributeRotations;
      // Pure coordinate + angle rotation of already-validated entities.
      // `rotateEntityAngles` adjusts start/end/rotation angles of Arcs/
      // Text/Dimensions/etc. within their typed ranges. T6c: in-place — no
      // entity added/removed, no id/spaceId change → invariants preserved by
      // construction → fast-path assembler. T6d-1: routed through the shared
      // in-place helper (O(touched), no full-document index build).
      return applyInPlaceTransformWithMutations(
        geometryDocument,
        new Set(entityIds),
        (entity) => {
          const rotated = rotateEntityAngles(
            transformEntityCoords(entity, (p) =>
              rotatePoint(p, basePoint, angleDeg),
            ),
            angleDeg,
          );
          if (
            restoreAttributeRotations &&
            Object.prototype.hasOwnProperty.call(
              restoreAttributeRotations,
              entity.id,
            ) &&
            rotated.type === "Block"
          ) {
            const perAttribute = restoreAttributeRotations[entity.id]!;
            return {
              ...rotated,
              attributes: rotated.attributes.map((attr, index) => {
                const key = String(index);
                if (!Object.prototype.hasOwnProperty.call(perAttribute, key)) {
                  return attr;
                }
                const captured = perAttribute[key];
                if (captured === null) {
                  const { overrideRotation: _drop, ...rest } = attr;
                  void _drop;
                  return rest;
                }
                return { ...attr, overrideRotation: captured };
              }),
            };
          }
          return rotated;
        },
      );
    }

    // ── SCALE ───────────────────────────────────────────────────────
    case "geometry/scale-entities": {
      const { entityIds, basePoint, factor } = command;
      // GitHub Codex review, PR #558 round 7 P2 — undo restore map for a
      // Block entity's per-attribute WORLD `overrideHeight` values (entityId
      // → { [attribute array index]: original overrideHeight }, `null`
      // sentinel = the source attribute had NO overrideHeight field at
      // all). Populated ONLY by `computeInverse`'s "geometry/scale-entities"
      // case; absent on every forward (user/AI) dispatch, so this block is
      // undo-only. See `presencePreservingValue`'s doc comment for why
      // SCALE's own factor arithmetic (`factor` then `1 / factor`) is
      // exactly self-inverse and yet still needs this snapshot: presence
      // preservation means the blind factor-inversion undo alone cannot
      // tell "restore to absent" apart from "restore to an explicit 2.5" —
      // same story as ROTATE's `restoreAttributeRotations` directly above.
      const restoreAttributeHeights = command.restoreAttributeHeights;
      // GitHub Codex review PR #558 round 2 (round-14 local read-only
      // follow-up) P2 — the rotation counterpart of `restoreAttributeHeights`
      // directly above (`scaleEntityRadii.ts`'s own doc: a negative `factor`
      // bumps a WORLD attribute's `overrideRotation` by 180°, presence-
      // preserving). Populated ONLY by `computeInverse` when the ORIGINAL
      // forward factor was negative; absent (and this block a no-op) for
      // every positive-factor scale.
      const restoreAttributeRotations = command.restoreAttributeRotations;
      // GitHub Codex review PR #794 round 16 P2 — issue #804. SCALE now
      // carries an MTEXT's cached measurement (DXF groups 42/43) with the
      // block it resizes and CLEARS it where the scaled block is not
      // geometrically similar (`scaleEntityRadii`'s MText arm +
      // `drawing-model::mtextUniformScalePreservesLayoutShape`). The SCALED
      // case is self-inverse through the same `factor` then `1 / factor`
      // arithmetic `nominalHeight` rides, but the CLEARED case is not
      // invertible at all — the undo re-dispatch would multiply the cleared
      // `0` by `1 / factor` and the measurement would be lost for good,
      // making the edit lossy rather than undoable. `computeInverse`
      // snapshots the PRE-scale pair of exactly the entities the forward
      // clears; this restores them, AFTER `scaleEntityRadii` has run on the
      // undo dispatch (see below) — applying it before would let the forward
      // logic clear the restored pair straight back to 0. Populated ONLY by
      // `computeInverse`, so this is undo-only, same as the two maps above.
      const restoreMtextMeasurements = command.restoreMtextMeasurements;
      // Pure coord + radius scaling — same safety story as MOVE. T6c: in-place
      // — no entity added/removed, no id/spaceId change → invariants preserved
      // by construction → fast-path assembler. T6d-1: routed through the shared
      // in-place helper (O(touched), no full-document index build).
      return applyInPlaceTransformWithMutations(
        geometryDocument,
        new Set(entityIds),
        (entity) => {
          // GitHub Codex review PR #785 round 2, P2 — "Scale the table
          // contents along with its insertion point." A `Table` cannot be
          // RESIZED: `rowHeights` (141) / `columnWidths` (142) are read-only
          // projections of `contentGroups`, and the visible geometry is the
          // pre-evaluated `*T<N>` graphics block (`tableAsBlockReference`,
          // unit scale) for which there is no generator — so writing 141/142
          // alone would desync the data from the picture, which is worse than
          // the pre-fix half-transform (insertion point scaled, contents not).
          // KNOWN INCOMPLETE, documented per CLAUDE.md ПРИОРИТЕТ #1's one
          // allowed exception; `command-engine/tableScaleRefusal.ts`'s header
          // carries the desync proof and the completion plan. DECLINE it here
          // (`null` ⇒ untouched, no mutation row, so the surrounding undo is
          // exact) — every OTHER selected entity still scales normally — and
          // let `geometryShellReducer`'s twin call report the refusal to the
          // user in RU+EN. `|factor| === 1` is NOT refused: it is either a
          // no-op or a point reflection, both of which this arm already
          // performs exactly (see the predicate's own doc).
          if (isTableScaleRefused(entity, factor)) return null;
          const scaled = scaleEntityRadii(
            transformEntityCoords(entity, (p) =>
              scalePoint(p, basePoint, factor),
            ),
            basePoint,
            factor,
          );
          // #804 — the cleared-measurement restore, applied to the OUTPUT of
          // `scaleEntityRadii` (the line above) so the forward clear has
          // already happened and this is the last word on the pair.
          const restoredMeasurement =
            restoreMtextMeasurements &&
            Object.prototype.hasOwnProperty.call(
              restoreMtextMeasurements,
              entity.id,
            )
              ? restoreMtextMeasurements[entity.id]!
              : undefined;
          if (restoredMeasurement && scaled.type === "MText") {
            return {
              ...scaled,
              horizontalWidth: restoredMeasurement.horizontalWidth,
              verticalHeight: restoredMeasurement.verticalHeight,
            };
          }
          const perAttributeHeight =
            restoreAttributeHeights &&
            Object.prototype.hasOwnProperty.call(
              restoreAttributeHeights,
              entity.id,
            )
              ? restoreAttributeHeights[entity.id]!
              : undefined;
          const perAttributeRotation =
            restoreAttributeRotations &&
            Object.prototype.hasOwnProperty.call(
              restoreAttributeRotations,
              entity.id,
            )
              ? restoreAttributeRotations[entity.id]!
              : undefined;
          if (
            (perAttributeHeight || perAttributeRotation) &&
            scaled.type === "Block"
          ) {
            return {
              ...scaled,
              attributes: scaled.attributes.map((attr, index) => {
                const key = String(index);
                let next = attr;
                if (
                  perAttributeHeight &&
                  Object.prototype.hasOwnProperty.call(perAttributeHeight, key)
                ) {
                  const captured = perAttributeHeight[key];
                  if (captured === null) {
                    const { overrideHeight: _drop, ...rest } = next;
                    void _drop;
                    next = rest;
                  } else {
                    next = { ...next, overrideHeight: captured };
                  }
                }
                if (
                  perAttributeRotation &&
                  Object.prototype.hasOwnProperty.call(perAttributeRotation, key)
                ) {
                  const captured = perAttributeRotation[key];
                  if (captured === null) {
                    const { overrideRotation: _drop, ...rest } = next;
                    void _drop;
                    next = rest;
                  } else {
                    next = { ...next, overrideRotation: captured };
                  }
                }
                return next;
              }),
            };
          }
          return scaled;
        },
      );
    }

    // ── OFFSET ──────────────────────────────────────────────────────
    case "geometry/offset-entity": {
      const { entityId, distance, sidePoint } = command;
      // T6a perf: once-seeded allocator + single batch append. OFFSET matches a
      // single target id, so `offsetClones` holds 0 or 1 entity — the batch
      // helper degenerates to the single-append cost while dropping the per-call
      // O(N) nextGeometryEntityId scan.
      const idAllocator = createEntityIdAllocator(geometryDocument);
      const offsetClones: DrawingEntity[] = [];
      const addedIds: string[] = [];

      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (entity.id !== entityId) continue;

          let offsetEntity: DrawingEntity | null = null;

          switch (entity.type) {
            case "Line": {
              const seg = offsetLineSegment(
                entity.start,
                entity.end,
                distance,
                sidePoint,
              );
              offsetEntity = { ...entity, start: seg.start, end: seg.end };
              break;
            }
            case "Circle": {
              const result = offsetCircle(
                entity.center,
                entity.radius,
                distance,
                sidePoint,
              );
              offsetEntity = {
                ...entity,
                center: result.center,
                radius: result.radius,
              };
              break;
            }
            case "Arc": {
              const result = offsetCircle(
                entity.center,
                entity.radius,
                distance,
                sidePoint,
              );
              offsetEntity = {
                ...entity,
                center: result.center,
                radius: result.radius,
              };
              break;
            }
            case "LWPOLYLINE": {
              // Offset every SPAN of the polyline in its own alphabet — a
              // straight span to a parallel one, a bulged span to a CONCENTRIC
              // arc — and re-join the neighbours: the shared
              // `command-engine/polylineOffsetRing.ts` (one truth with the
              // heavy `Polyline` arm below).
              if (entity.vertices.length < 2) break;
              const verts = entity.vertices;
              const newPoints = offsetPolylineVertexRing(
                verts,
                entity.closed,
                distance,
                sidePoint,
              );
              // Spread each SOURCE vertex so every per-vertex channel the
              // record carried — 40/41 widths, the group-91 vertex id —
              // survives the offset. Group 42 is the ONE channel the offset
              // itself owns (@codex PR #785 round 16, FINDING 1): a trimmed
              // arc span sweeps a different angle about the same centre, so
              // the ring hands back a new bulge for it and `undefined` for
              // every span whose sweep is unchanged — which is presence-
              // preserving, so a straight polyline's records come through
              // byte-identical.
              const newVerts = newPoints.map((p, i) => ({
                ...verts[i],
                x: p.x,
                y: p.y,
                ...(p.bulge === undefined ? {} : { bulge: p.bulge }),
              }));

              offsetEntity = { ...entity, vertices: newVerts };
              break;
            }
            case "Polyline": {
              // Heavy POLYLINE — @codex PR #785 round 14, "Support 2D heavy
              // polylines in OFFSET". `isSupportedHeavyPolylineCurve` is the
              // ONE eligibility truth (shared with the picker gate and the
              // shell reducer's RU+EN audit row): a 3D polyline and both mesh
              // subtypes are what AutoCAD ITSELF declines ("Cannot offset that
              // object."), and a curve/spline-fit polyline is a documented
              // known-incomplete subsystem. A declined polyline leaves
              // `offsetEntity` null, so this reducer appends nothing and the
              // shell arm reports the reason — never a silent no-op.
              if (!isSupportedHeavyPolylineCurve(entity, "offset")) break;
              // The drawn chain (`command-engine/heavyPolylineDrawnChain.ts`,
              // through `cad-core`'s `heavyPolylineRuns`) is the same
              // projection TRIM/EXTEND and the ARRAYPATH sampler read, so all
              // three agree on which records this polyline draws. For an
              // ACCEPTED subtype the run is every record in file order, which
              // is why `indices[k]` below addresses `entity.vertices` directly.
              const chain = heavyPolylineDrawnChain(entity);
              if (!chain) break;
              const newPoints = offsetPolylineVertexRing(
                chain.vertices,
                chain.closed,
                distance,
                sidePoint,
              );
              // Write back onto the SOURCE records so each VERTEX keeps its
              // widths, flags, XDATA, extension dictionary, reactors and
              // unmapped groups; `cloneEntityWithFreshIdentity` below is what
              // strips the own group-5 handles the clone must not share.
              // `location.z` is the record's OCS duplicate of the entity
              // elevation and is deliberately untouched — OFFSET moves the
              // curve inside its own plane, exactly as the LWPOLYLINE arm
              // leaves `elevation` alone.
              // Group 42 is the one channel the offset OWNS rather than
              // carries (@codex PR #785 round 16, FINDING 1) — and it is
              // written PRESENCE-PRESERVINGLY: `vertexSchema.bulge` is
              // `.optional()` on a heavy record, so a span whose sweep the
              // offset did not change leaves the field exactly as the file
              // had it (absent stays absent) rather than materialising a 0.
              const newVertices = entity.vertices.slice();
              chain.indices.forEach((recordIndex, k) => {
                const source = entity.vertices[recordIndex];
                const p = newPoints[k];
                newVertices[recordIndex] = {
                  ...source,
                  location: { ...source.location, x: p.x, y: p.y },
                  ...(p.bulge === undefined ? {} : { bulge: p.bulge }),
                };
              });
              offsetEntity = { ...entity, vertices: newVertices };
              break;
            }
            default:
              // Unsupported entity type for OFFSET — no-op
              break;
          }

          if (offsetEntity) {
            const typePrefix = entity.type.toLowerCase().replace(/\s+/g, "-");
            const newId = idAllocator.next(typePrefix);
            // T9b review round 3 / @codex PR #785 round 3 — a clone must not
            // share its source's NESTED own group-5 handles. A plain
            // `{ ...offsetEntity, id }` was safe while OFFSET knew only
            // Line / Circle / Arc / LWPOLYLINE (none of which owns a child
            // record), and is NOT once a heavy POLYLINE — which owns every
            // VERTEX handle and its terminating SEQEND — can reach here:
            // two records with the same group-5 on export is a corrupt file.
            // `resetNestedRecordIdentity` finds those slots STRUCTURALLY, so
            // the four simple families pass through it unchanged and the id
            // sequence stays byte-identical.
            offsetClones.push(cloneEntityWithFreshIdentity(offsetEntity, newId));
            addedIds.push(newId);
          }
        }
      }
      const resultDoc = appendGeometryEntities(geometryDocument, offsetClones);
      return {
        document: resultDoc,
        mutations: buildGeometryMutations(
          geometryDocument,
          resultDoc,
          addedIds,
          [],
          [],
        ),
      };
    }

    default:
      return null;
  }
}
